import type { GrupoVagas, SuplenteLinha } from './types';

// Motivos que a direção escolhe ao cancelar. O "Outro" exige texto livre.
export const MOTIVOS_CANCELAMENTO = [
  'Desistiu',
  'Não pagou no prazo',
  'Problema de saúde',
  'Inscrição duplicada',
  'Pedido da direção',
  'Outro',
] as const;

export const MOTIVO_OUTRO = 'Outro';

// O que a folha da pessoa precisa saber sobre as vagas do evento.
export interface ContextoVagas {
  temVagas: boolean;
  ehSuplente: boolean;
  temVaga: boolean;
  grupoNome: string | null;
  primeiroSuplente: { inscricao_id: number; nome: string } | null;
}

// Recado que sobe para a lista (toast) depois de uma ação.
export interface AvisoVagas {
  texto: string;
  /** Quando existe, o toast ganha um botão que dá a vaga a esta inscrição. */
  inscricaoId?: number;
  acaoRotulo?: string;
}

// Chave que identifica o grupo (modalidade + opção/grupo).
export function chaveGrupo(modalidadeId: number, opcaoId: number | null | undefined): string {
  return `${Number(modalidadeId)}:${opcaoId ?? 'null'}`;
}

// Nome bonito do grupo: o grupo (Tupã · Feminino) ou a própria participação.
export function nomeDoGrupo(g: { grupo: string | null; modalidade: string }): string {
  return g.grupo || g.modalidade;
}

// Nome completo do grupo: "Participação · Grupo" quando os dois existem.
export function nomeCompletoDoGrupo(g: {
  grupo: string | null;
  modalidade: string;
}): string {
  return g.grupo ? `${g.modalidade} · ${g.grupo}` : g.modalidade;
}

// O banco devolve só o número da senha; aqui reconstruímos o texto ("M42").
// O prefixo e a largura vêm de uma senha já existente no grupo.
export function formatarSenha(num: number, exemplo?: string | null): string {
  const n = Number(num);
  if (!Number.isFinite(n)) return String(exemplo ?? '');
  if (!exemplo) return String(n);
  const digitos = exemplo.replace(/\D/g, '');
  if (!digitos) return String(n);
  const prefixo = exemplo.slice(0, exemplo.length - digitos.length);
  return `${prefixo}${String(n).padStart(digitos.length, '0')}`;
}

// Uma senha de exemplo por grupo (para reconstruir o prefixo das senhas).
export function exemplosDeSenha(fila: SuplenteLinha[]): Map<string, string> {
  const mapa = new Map<string, string>();
  fila.forEach((f) => {
    const chave = chaveGrupo(f.modalidade_id, f.opcao_id);
    if (f.senha && !mapa.has(chave)) mapa.set(chave, f.senha);
  });
  return mapa;
}

// Fila de suplentes de um grupo, em ordem de posição.
export function filaDoGrupo(fila: SuplenteLinha[], grupo: GrupoVagas): SuplenteLinha[] {
  const chave = chaveGrupo(grupo.modalidade_id, grupo.opcao_id);
  return fila
    .filter((f) => chaveGrupo(f.modalidade_id, f.opcao_id) === chave)
    .sort((a, b) => a.posicao - b.posicao);
}

// Chave de contagem de uma senha dentro do grupo. Normaliza "M07" e 7 para o mesmo número.
export function chaveSenha(chave: string, valor: number | string): string {
  const digitos = String(valor).replace(/\D/g, '');
  return `${chave}:${Number(digitos || 0)}`;
}

// "1º", "2º", "3º"...
export function ordinal(posicao: number): string {
  return `${posicao}º`;
}